import React from 'react';
import { usePageMeta, organizationSchema, breadcrumbSchema } from '../lib/seo';
import { siteConfig, legalLine } from '../siteConfig';
import { integrations, portfolio } from '../data/siteData';
import { BookCallForm } from '../components/BookCallModal';
import {
  AnswerBlock,
  ButtonLink,
  Card,
  Container,
  CTASection,
  FeatureList,
  PageHero,
  Section,
  SectionHeading,
} from '../components/SiteUi';

export function AboutPage() {
  usePageMeta({
    title: 'About CloudNext – Australian Software Studio',
    description: 'CloudNext is an Australia-based software studio in Hervey Bay, Queensland, building AI receptionists, websites and custom software.',
    path: '/about',
    jsonLd: [
      organizationSchema(),
      {
        '@type': 'AboutPage',
        name: 'About CloudNext',
        url: `${siteConfig.url}/about`,
        about: { '@id': `${siteConfig.url}/#organization` },
      },
      breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }]),
    ],
  });

  return (
    <>
      <PageHero
        eyebrow="About"
        title={<>A software studio that also <span className="text-gold-gradient">answers the phone.</span></>}
        description="CloudNext is an Australia-based software studio. We build products and we set up and run a service for trades and small businesses."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'About' }]}
      />

      <Section>
        <Container>
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <SectionHeading eyebrow="The founder" title="Orfeo Hizon" />
              <div className="mt-5 space-y-4 text-base leading-7 text-dust">
                <p>
                  I have spent more than 20 years building software, including enterprise systems, mobile apps, SaaS products and AI tools. CloudNext is the studio I run from Hervey Bay, Queensland.
                </p>
                <p>
                  The service exists because I kept seeing the same problem in small businesses: the website, the phone and the follow-up were treated as separate things. A lead would arrive and sit there, or a call would ring out while the owner was on the tools.
                </p>
                <p>
                  BulletChat is our self-service chatbot platform. CloudNext is the service that sets it up, trains it, connects the booking and manages it for you. 
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <ButtonLink to="/contact">Book a call</ButtonLink>
                <ButtonLink href={siteConfig.founderLinkedin} variant="secondary" target="_blank" rel="noopener noreferrer">LinkedIn</ButtonLink>
              </div>
            </div>
            <Card>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-gold-light">The basics</p>
              <div className="mt-4 space-y-4 text-sm leading-6 text-dust">
                <p><span className="text-cream">Owner:</span> {siteConfig.legalName} (sole trader)</p>
                {siteConfig.abn ? <p><span className="text-cream">Business name:</span> CloudNext</p> : <p><span className="text-cream">Brand:</span> CloudNext</p>}
                <p><span className="text-cream">Location:</span> {siteConfig.location}</p>
                <p><span className="text-cream">Service area:</span> {siteConfig.serviceArea}</p>
                <p><span className="text-cream">Email:</span> <a href={`mailto:${siteConfig.email}`} className="text-gold-light">{siteConfig.email}</a></p>
                {siteConfig.abn ? <p><span className="text-cream">ABN:</span> {siteConfig.abn}</p> : null}
              </div>
              <p className="mt-6 border-t border-white/[0.06] pt-4 text-xs leading-5 text-ash">{legalLine}</p>
            </Card>
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <SectionHeading eyebrow="How we work" title="Direct, fixed-fee, and clear about the limits." />
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              { title: 'Direct communication', text: 'You talk to the person doing the work, not a layer of account managers.' },
              { title: 'Fixed fee for defined work', text: 'The scope and price are agreed before work starts. Larger projects can use milestones.' },
              { title: 'AI-assisted, human-reviewed', text: 'We use AI to move faster, but a person reviews the work and owns the outcome.' },
            ].map((item) => (
              <Card key={item.title}>
                <h3 className="font-display text-lg text-cream">{item.title}</h3>
                <p className="mt-2 text-sm leading-6 text-dust">{item.text}</p>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeading eyebrow="The products" title="Software we build and run." />
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <Card>
              <h3 className="font-display text-xl text-cream">BulletChat</h3>
              <p className="mt-2 text-sm leading-6 text-dust">A self-service chatbot platform for small businesses. Train a chatbot on your site and add it without a developer.</p>
              <ButtonLink href={siteConfig.bulletchat} variant="ghost" size="sm" className="mt-4 px-0" target="_blank" rel="noopener noreferrer">Visit BulletChat →</ButtonLink>
            </Card>
            <Card>
              <h3 className="font-display text-xl text-cream">Loope</h3>
              <p className="mt-2 text-sm leading-6 text-dust">Job management for trade businesses: scheduling, invoicing and Xero. Ask whether it fits your workflow.</p>
              <ButtonLink href="https://loope.app" variant="ghost" size="sm" className="mt-4 px-0" target="_blank" rel="noopener noreferrer">Visit Loope →</ButtonLink>
            </Card>
          </div>
        </Container>
      </Section>

      <CTASection title="Want to work with us?" description="Tell us what you need. If it is not a fit, we will say so." primaryTo="/contact" primaryLabel="Contact CloudNext" secondaryTo="/work" secondaryLabel="See the work" />
    </>
  );
}

export function ContactPage() {
  usePageMeta({
    title: 'Contact CloudNext – Book a Call',
    description: 'Tell us where the calls or leads are getting lost. Book a 15-minute call with CloudNext.',
    path: '/contact',
    jsonLd: [
      organizationSchema(),
      breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }]),
    ],
  });

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title={<>Tell us where the <span className="text-gold-gradient">leads are getting lost.</span></>}
        description="A short call is enough to work out whether an AI receptionist, a website, a chatbot or a custom build is the right next move."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Contact' }]}
      />

      <Section>
        <Container>
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <SectionHeading eyebrow="What happens next" title="No pressure, no 40-page proposal." />
              <FeatureList
                className="mt-6"
                items={[
                  'We reply within one business day.',
                  'A 15-minute call to understand the current setup.',
                  'A clear recommendation: AI receptionist, website, chatbot, custom build, or nothing yet.',
                  'If it fits, a written scope and quote.',
                ]}
              />
              <div className="mt-8 space-y-3 text-sm text-dust">
                <p><span className="text-cream">Email:</span> <a href={`mailto:${siteConfig.email}`} className="text-gold-light">{siteConfig.email}</a></p>
                <p><span className="text-cream">Location:</span> {siteConfig.location}</p>
                <p><span className="text-cream">Service area:</span> {siteConfig.serviceArea}</p>
                {siteConfig.phone ? <p><span className="text-cream">Phone:</span> <a href={`tel:${siteConfig.phone.replace(/\s+/g, '')}`} className="text-gold-light">{siteConfig.phone}</a></p> : null}
              </div>
              {siteConfig.demoPhone ? (
                <div className="mt-6 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 text-sm leading-6 text-dust">
                  <p className="font-medium text-cream">Prefer to hear it first?</p>
                  <p className="mt-2">The live demo lets you call the AI and try to break it before you talk to us.</p>
                  <ButtonLink to="/demo" variant="ghost" size="sm" className="mt-3 px-0">Go to the demo →</ButtonLink>
                </div>
              ) : null}
            </div>
            <div className="rounded-3xl border border-white/[0.07] bg-white/[0.02] p-6 md:p-8">
              <h2 className="font-display text-2xl text-cream">Book a call</h2>
              <div className="mt-6">
                <BookCallForm source="contact_page" />
              </div>
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}

export function PressPage() {
  usePageMeta({
    title: 'Press and Media – CloudNext',
    description: 'Founder bio, company facts, logos and media contact for CloudNext.',
    path: '/press',
    jsonLd: [organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Press', path: '/press' }])],
  });

  return (
    <>
      <PageHero
        eyebrow="Press"
        title={<>CloudNext <span className="text-gold-gradient">facts and media.</span></>}
        description="A short, accurate record for journalists, partners and anyone checking who we are."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Press' }]}
      />
      <Section>
        <Container>
          <div className="grid gap-8 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="Company facts" title="The short version." />
              <div className="mt-6 space-y-3 text-sm leading-6 text-dust">
                <p><span className="text-cream">Name:</span> CloudNext</p>
                <p><span className="text-cream">Owner:</span> {siteConfig.legalName} (sole trader)</p>
                <p><span className="text-cream">Location:</span> {siteConfig.location}</p>
                <p><span className="text-cream">What we do:</span> We set up and run AI receptionists, websites and chatbots for trades; custom software, MVPs and SaaS.</p>
                <p><span className="text-cream">Product:</span> BulletChat, a self-service chatbot platform.</p>
                <p><span className="text-cream">Media contact:</span> <a href={`mailto:${siteConfig.email}`} className="text-gold-light">{siteConfig.email}</a></p>
              </div>
              <p className="mt-6 text-xs leading-5 text-ash">{legalLine}</p>
            </div>
            <Card>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-gold-light">Founder bio</p>
              <p className="mt-3 text-sm leading-6 text-dust">
                Orfeo Hizon is the founder of CloudNext. He has more than 20 years of software experience across enterprise systems, mobile apps, SaaS products and AI tools. He is based in Hervey Bay, Queensland.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <ButtonLink href={siteConfig.founderLinkedin} variant="secondary" size="sm" target="_blank" rel="noopener noreferrer">Founder LinkedIn</ButtonLink>
                <ButtonLink href={siteConfig.linkedin} variant="secondary" size="sm" target="_blank" rel="noopener noreferrer">Company LinkedIn</ButtonLink>
              </div>
              <div className="mt-4 flex flex-wrap gap-3 text-xs text-dust">
                <a href="/Icon-512.png" download className="hover:text-gold-light">Download logo (512px)</a>
                <a href="/Icon-192.png" download className="hover:text-gold-light">Download logo (192px)</a>
              </div>
            </Card>
          </div>
        </Container>
      </Section>
    </>
  );
}

export function WorkPage() {
  usePageMeta({
    title: 'Work – Websites and Software by CloudNext',
    description: 'A selection of websites and software built by CloudNext. Demo sites are labelled separately from client work.',
    path: '/work',
    jsonLd: [organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Work', path: '/work' }])],
  });

  return (
    <>
      <PageHero
        eyebrow="Work"
        title={<>Websites and software <span className="text-gold-gradient">we have built.</span></>}
        description="Client websites, demo websites and products. Demo sites are labelled so you know what is client work and what is a demonstration."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Work' }]}
      />
      <Section>
        <Container>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {portfolio.map((site) => (
              <a key={site.url} href={site.url} target="_blank" rel="noopener noreferrer" className="group overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] transition hover:-translate-y-0.5 hover:border-gold/40">
                <div className="flex items-center justify-between gap-2 border-b border-white/[0.06] px-4 py-3 text-xs text-ash">
                  <span className="truncate">{site.url.replace(/^https?:\/\//, '').replace(/\/.*$/, '')}</span>
                  <span className={`rounded-full px-2 py-0.5 ${site.type === 'Demo website' ? 'bg-amber-400/10 text-amber-200' : site.type === 'Product' ? 'bg-violet-400/10 text-violet-200' : 'bg-emerald-400/10 text-emerald-200'}`}>{site.type}</span>
                </div>
                <img src={site.image} alt={`${site.title} preview`} className="h-44 w-full object-cover object-top" loading="lazy" />
                <div className="p-5">
                  <h3 className="font-display text-lg text-cream group-hover:text-gold-light">{site.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-dust">{site.description}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {site.tags.map((tag) => <span key={tag} className="rounded-full border border-white/[0.06] px-2.5 py-0.5 text-[11px] text-dust">{tag}</span>)}
                  </div>
                  <span className="mt-4 inline-block text-sm text-gold-light">Visit →</span>
                </div>
              </a>
            ))}
          </div>
        </Container>
      </Section>
      <CTASection title="Need something like this?" description="Tell us what the site or software needs to do. We will tell you what fits." primaryTo="/contact" primaryLabel="Start a project" secondaryTo="/custom-software" secondaryLabel="See custom software" />
    </>
  );
}

export function IntegrationsPage() {
  usePageMeta({
    title: 'AI Receptionist and Chatbot Integrations – CloudNext',
    description: 'How CloudNext connects AI receptionists and chatbots to calendars, job management, CRM and accounting systems.',
    path: '/integrations',
    jsonLd: [organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Integrations', path: '/integrations' }])],
  });

  return (
    <>
      <PageHero
        eyebrow="Integrations"
        title={<>It has to land in the <span className="text-gold-gradient">system you already use.</span></>}
        description="A booking is only useful if it reaches your calendar or job system. The connection depends on the setup, and we confirm what is live before you sign."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Integrations' }]}
      />
      <Section>
        <Container>
          <div className="space-y-4">
            {integrations.map((group) => (
              <Card key={group.group}>
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h3 className="font-display text-xl text-cream">{group.group}</h3>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {group.items.map((item) => <span key={item} className="rounded-full border border-white/[0.06] bg-white/[0.02] px-3 py-1 text-xs text-dust">{item}</span>)}
                    </div>
                  </div>
                  <span className="w-fit rounded-full border border-gold/20 bg-gold/10 px-3 py-1 text-xs text-gold-light">{group.status}</span>
                </div>
              </Card>
            ))}
          </div>
          <div className="mt-8">
            <AnswerBlock>
              Some connections need extra setup. We quote them before you sign. We confirm the exact system and access first, and we do not claim a connection that has not been tested end to end.
            </AnswerBlock>
          </div>
        </Container>
      </Section>
      <CTASection title="Need a specific connection?" description="Tell us what you use and what needs to happen after a call or booking." primaryTo="/contact" primaryLabel="Ask about a connection" secondaryTo="/ai-receptionist" secondaryLabel="See the AI receptionist" />
    </>
  );
}

export function HerveyBayPage() {
  usePageMeta({
    title: 'AI Receptionist and Websites in Hervey Bay – CloudNext',
    description: 'CloudNext is based in Hervey Bay and serves clients across Australia. Setup waived for the first five trades businesses until 31 December 2026.',
    path: '/hervey-bay',
    jsonLd: [organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Hervey Bay', path: '/hervey-bay' }])],
  });

  return (
    <>
      <PageHero
        eyebrow="Hervey Bay"
        title={<>Local roots. <span className="text-gold-gradient">Australia-wide service.</span></>}
        description="CloudNext is based in Hervey Bay, Queensland. We meet local clients in person and work with businesses across Australia remotely."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Hervey Bay' }]}
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <ButtonLink to="/contact">Book a local meeting</ButtonLink>
          <ButtonLink to="/demo" variant="secondary">Hear the demo</ButtonLink>
        </div>
      </PageHero>
      <Section>
        <Container>
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="Local proof" title="We are building the first local case studies now." />
              <p className="mt-5 text-sm leading-6 text-dust">
                The AI receptionist is live. The first five trades businesses can take up the setup waiver until 31 December 2026. The case studies will be published with permission, with real numbers and dates.
              </p>
              <FeatureList
                className="mt-6"
                items={[
                  'In-person meetings by appointment',
                  'Local trades first: plumbing, electrical, HVAC',
                  'Remote setup available across Australia',
                                    'Terms confirmed before you sign',
                ]}
              />
            </div>
            <Card>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-gold-light">What we can do locally</p>
              <FeatureList
                className="mt-4"
                items={[
                  'Review the current phone and website setup',
                  'Set up a local trade demo',
                  'Test how calls are handled on a local number',
                  'Meet to review the first weeks',
                  'Connect the booking to the calendar or job system',
                ]}
              />
            </Card>
          </div>
        </Container>
      </Section>
      <CTASection title="Want to be one of the first Hervey Bay setups?" description="Tell us about the business and the calls you miss." primaryTo="/contact" primaryLabel="Book a local meeting" secondaryTo="/pricing" secondaryLabel="See pricing" />
    </>
  );
}

export function BulletChatPage() {
  usePageMeta({
    title: 'BulletChat and CloudNext – Chatbot Set Up for You',
    description: 'BulletChat is CloudNext’s self-service chatbot platform. CloudNext is the service that sets up, trains and manages it.',
    path: '/bullet-chat',
    jsonLd: [organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'BulletChat', path: '/bullet-chat' }])],
  });

  return (
    <>
      <PageHero
        eyebrow="BulletChat"
        title={<><span className="text-gold-gradient">Self-service platform.</span> We set it up and run it.</>}
        description="BulletChat is our self-service chatbot platform. CloudNext is the service that sets it up, trains it, connects the booking and manages it for you."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'BulletChat' }]}
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <ButtonLink href={siteConfig.bulletchat} target="_blank" rel="noopener noreferrer">Visit BulletChat</ButtonLink>
          <ButtonLink to="/website-chatbot" variant="secondary">See the managed service</ButtonLink>
        </div>
      </PageHero>
      <Section>
        <Container>
          <div className="grid gap-5 md:grid-cols-2">
            <Card>
              <h3 className="font-display text-xl text-cream">Use BulletChat yourself</h3>
              <p className="mt-2 text-sm leading-6 text-dust">Create a chatbot, train it on your website content and add it to your site without a developer. Good if you want to run it yourself.</p>
              <ButtonLink href={siteConfig.bulletchat} variant="ghost" size="sm" className="mt-4 px-0" target="_blank" rel="noopener noreferrer">Go to bulletchat.app →</ButtonLink>
            </Card>
            <Card>
              <h3 className="font-display text-xl text-cream">Have CloudNext do it for you</h3>
              <p className="mt-2 text-sm leading-6 text-dust">We set up the website, chatbot, AI receptionist, booking and reporting, then manage it. Good if you want the result without the setup work.</p>
              <ButtonLink to="/website-chatbot" variant="ghost" size="sm" className="mt-4 px-0">See the managed service →</ButtonLink>
            </Card>
          </div>
        </Container>
      </Section>
      <CTASection title="Not sure which one you need?" description="Tell us what you want to handle and we will tell you whether you run it yourself or have us set it up and run it." primaryTo="/contact" primaryLabel="Ask CloudNext" secondaryTo="/pricing" secondaryLabel="See pricing" />
    </>
  );
}

export function CaseStudiesPage() {
  usePageMeta({
    title: 'CloudNext Case Studies – Trades AI Receptionists',
    description: 'CloudNext case studies for AI receptionists. Real numbers and dates, shared only with permission.',
    path: '/case-studies',
    jsonLd: [organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Case Studies', path: '/case-studies' }])],
  });

  return (
    <>
      <PageHero
        eyebrow="Case studies"
        title={<>The first case studies are <span className="text-gold-gradient">being built now.</span></>}
        description="The setup waiver covers the first five trades businesses. We will publish real case studies with permission, real numbers and dates. No invented clients."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Case Studies' }]}
      />
      <Section>
        <Container>
          <div className="grid gap-8 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="What a case study will include" title="The problem, the setup and the numbers." />
              <FeatureList
                className="mt-6"
                items={[
                  'The business and the trade',
                  'The calls that were being missed',
                  'How calls are handled and booking rules',
                  'Connections to your other software and what happens when it can’t answer',
                  'Calls answered, bookings made and after-hours share',
                  'A quote from the owner, with permission',
                ]}
              />
            </div>
            <Card>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-gold-light">Why there are no case studies yet</p>
              <p className="mt-4 text-sm leading-6 text-dust">
                The AI receptionist is live, but we are not going to pretend we have a track record we do not have. The first five clients help us build that proof properly.
              </p>
              <p className="mt-4 text-sm leading-6 text-dust">
                If you want to be one of the first five, we will agree the scope and the price before anything starts.
              </p>
              <ButtonLink to="/contact" className="mt-6">Ask about the setup waiver</ButtonLink>
            </Card>
          </div>
        </Container>
      </Section>
    </>
  );
}
